import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadMyPurchaseHistory from "@NA/Handlers/PurchaseHistory/LoadMyPurchaseHistory";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { FC, ReactNode } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import ADIProductGridCard from "./PurchaseHistory/ADIProductGridCard";
import ADIColors from "@NA/Utils/ADIColors";
import setPurchaseHistoryView from "@NA/Handlers/PurchaseHistory/SetPurchaseHistoryView";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ADIProductListCard from "./PurchaseHistory/ADIProductListCard";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import ProductCompareFlyOut from "@insite/content-library/Components/ProductCompareFlyOut";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import siteMessage from "@insite/client-framework/SiteMessage";
import Button from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import CrossIcon from "@NA/Icons/CrossIcon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";

const enum fields {
    showImage = "showImage",
    showBrand = "showBrand",
    showPartNumbers = "showPartNumbers",
    showCompare = "showCompare",
    showAvailability = "showAvailability",
    showPrice = "showPrice",
    productListDefaultView = "productListDefaultView",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImage]: boolean;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showCompare]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showPrice]: boolean;
        [fields.productListDefaultView]: ProductListViewType;
    };
}

const mapStateToProps = (state: CustomApplicationState, ownProps: OwnProps) => {
    const currentSplitPath = getLocation(state).pathname.split("/");
    const currentPath = currentSplitPath[currentSplitPath.length - 1];
    const settingsCollection = getSettingsCollection(state) as any;
    function getIsProductCompareFull(state: ApplicationState) {
        return state.components.compareProductsDrawer.productsIds.length >= 3;
    }

    return {
        isLoading: state.ADIGlobalData.myPurchaseHistoryStore.isLoading,
        purchaseHistoryData: state.ADIGlobalData.myPurchaseHistoryStore.myPurchaseHistory,
        productLine: state.ADIGlobalData.myPurchaseHistoryStore.myPurchasedProductInfosByLineId,
        view: state.ADIGlobalData.myPurchaseHistoryStore.view,
        isProductCompareFull: getIsProductCompareFull(state),
        purchaseHistoryConfigState: state.ADIGlobalData.myPurchaseHistoryConfig,
        currentPage: currentPath,
        productComparePageLink: getPageLinkByPageType(state, "ProductComparePage"),
        homeBranch: settingsCollection.maNavSettingSettings?.billtoDeafultWareHouseDescription?.toLowerCase() as string,
    };
};

const mapDispatchToProps = {
    setPurchaseHistoryView,
    loadMyPurchaseHistory,
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentlyPurchasedProductListStyles {
    cardGridContainer?: GridContainerProps;
    cardGridItem?: GridItemProps;
    cardListItem?: GridItemProps;
    gridCardDivider?: InjectableCss;
    wrapper?: InjectableCss;
    noProductsText?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    compareWarningPopup?: ModalPresentationProps;
    CompareButtonContainer?: InjectableCss;
    messageText?: InjectableCss
}

const RecentlyPurchasedProductList: FC<Props> = ({
    isLoading,
    purchaseHistoryData,
    productLine,
    fields,
    currentPage,
    homeBranch,
    id,
    view,
    loadMyPurchaseHistory,
    setPurchaseHistoryView,
    productComparePageLink,
    isProductCompareFull,
    purchaseHistoryConfigState,
}) => {
    React.useEffect(() => {
        if (view) { 
            setPurchaseHistoryView({ view }); 
        };
    }, []);

    const [maximumCompareReached, setMaximumCompareReached] = React.useState<boolean>(false);

    const closePopup = () => {
        setMaximumCompareReached(false);
    };

    const rcentlyPurchasedProductListStyles: RecentlyPurchasedProductListStyles = {
        cardGridContainer: {
            gap: 10,
            css: css`
                margin-top: 20px;
            `,
        },
        cardGridItem: {
            width: [12, 12, 4, 3, 3],
            css: css`
                padding: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            flex-basis: 100% !important;
                            max-width: 100% !important;
                        `,
                        css`
                            flex-basis: 50% !important;
                            max-width: 50% !important;
                        `,
                        css`
                            flex-basis: 33% !important;
                            max-width: 33% !important;
                        `,
                        css`
                            flex-basis: ${currentPage === "shopmybranch" ? "25% !important" : "20% !important"};
                            max-width: ${currentPage === "shopmybranch" ? "25% !important" : "20% !important"};
                        `,
                        css`
                            flex-basis: ${currentPage === "shopmybranch" ? "25% !important" : "20% !important"};
                            max-width: ${currentPage === "shopmybranch" ? "25% !important" : "20% !important"};
                        `,
                    ])}
            `,
        },
        cardListItem: {
            width: 12,
            css: css`
                padding: 0;
            `,
        },
        gridCardDivider: {
            css: css`
                padding: 10px;
                height: 100%;
                width: 100%;
                border-bottom: 0;
            `,
        },
        wrapper: {
            css: css`
                margin-top: 20px;
                background: ${ADIColors.white};
            `,
        },
        messageText: {
            css: css`
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                margin: 0 auto;
                .messageTitle {
                font-family: 'Nunito Sans';
                text-align: center;
                font-size: 20px;
                color: #666;
                font-weight: bold;
                padding: 0;
                font-stretch: normal;
                font-style: normal;
                line-height: 1.6;
                letter-spacing: normal;
                margin-bottom: 0;}
            `,

        },
        centeringWrapper: {
            css: css`
                width: 100%;
                height: 300px;
                display: flex;
                align-items: center;
            `,
        },
        spinner: {
            css: css`
                margin: auto;
            `,
        },
        noProductsText: {
            variant: "h4",
            css: css`
                display: block;
                margin: auto;
            `,
        },

        compareWarningPopup: {
            sizeVariant: "small",
            size: 600,
            closeButtonIconProps: {
                src: CrossIcon,
                size: 20,
            },
            cssOverrides: {
                modalContainer: css`
                    > div {
                        box-shadow: 0 1px 9px 0 rgba(0, 0, 0, 0.14);
                    border-radius: 7px;
                    max-width: 650px;
                    width: 65%;
                    height: auto;
                    top: 20%;
                    position: absolute;
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme, [css`top: 10%;width: 90%;`, css`top: 10%;width: 90%;`, css`top: 20%;width: 70%;`,],);
                    }}
                    }
                `,
                modalTitle: css`
                    border-bottom: 0;
                    padding: 20px 20px 5px;
                    span {
                        font-size: 28px;
                        font-weight: bold;
                        font-style: normal;
                        line-height: normal;
                        letter-spacing: normal;
                        color: #002856;
                        width: calc(100% - 35px);
                        display: inline-block;
                        text-transform: none;
                    }
                    button {
                        position: absolute;
                        top: 14px;
                        right: 32px;
                    }
                `,
                modalContent: css`
                    padding: 0 20px 20px;

                    ${TypographyStyle} {
                        font-size: 14px;
                        color: ${ADIColors.black};
                    }
                `,
            },
        },
        CompareButtonContainer: {
            css: css`
            text-align: right;
            margin-top: 20px;
            ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme, [css` text-align: left; `,],);
                }}
            a {
                display: inline-block;
                ${TypographyStyle} {
                    font-size: 16px;
                    color: ${ADIColors.white};
                }
            }
        `,
        },
    };

    const styles = rcentlyPurchasedProductListStyles;

    React.useEffect(() => {
        if (!isLoading && purchaseHistoryConfigState) {
            if (currentPage === "shopmybranch") {
                loadMyPurchaseHistory({
                    isShopMyBranch: true,
                    searchText: "",
                    pageName: "purchasehistory-ShopMyBranch",
                    pagination: {
                        ...purchaseHistoryConfigState.pagination,
                        pageSize: purchaseHistoryConfigState.pagination.pageSize,
                        currentPage: purchaseHistoryConfigState.pagination.currentPage,
                        defaultPageSize: purchaseHistoryConfigState.pagination.defaultPageSize,
                        page: purchaseHistoryConfigState.pagination.page,
                        totalItemCount: purchaseHistoryConfigState.pagination.totalItemCount,
                        sortType: purchaseHistoryConfigState.pagination.sortType
                            ? purchaseHistoryConfigState.pagination.sortType
                            : "Bestseller",
                    },
                });
            } else {
                loadMyPurchaseHistory({
                    toDate: purchaseHistoryConfigState.toDate,
                    fromDate: purchaseHistoryConfigState.fromDate,
                    searchText: purchaseHistoryConfigState.searchText,
                    pageName: "purchasehistory",
                    pagination: purchaseHistoryConfigState.pagination,
                });
            }
        }
    }, [purchaseHistoryConfigState]);

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} data-test-selector="RecentlyPurchasedProductListSpinner" />
            </StyledWrapper>
        );
    }

    if (!purchaseHistoryData?.products) {
        return null;
    }

    const branchTitleStyle = {
        css: css`
            font-size: 24px;
            font-weight: bold;
            line-height: normal;
            letter-spacing: normal;
            color: ${ADIColors.darkBlue1};
            margin-top: 27px;
            margin-bottom: 10px;
            .branchTitle {
                text-transform: capitalize;
            }
        `,
    };

    const products = purchaseHistoryData?.products;

    if (products?.length === 0) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.centeringWrapper} data-test-selector="productListNoneFound">
                    {currentPage === "shopmybranch" ? (
                        <StyledWrapper {...branchTitleStyle}>
                            {translate(`You have no recently purchased products in stock at the `)}
                            <Typography className="branchTitle">{homeBranch}</Typography>
                            {translate(` Branch`)}
                        </StyledWrapper>
                    ) : (
                        <StyledWrapper {...styles.messageText}>
                            <Typography className="messageTitle">{"We couldn't find any products matching your search criteria."}</Typography>
                            <Typography className="messageTitle">{"Try searching another item name or item number."}</Typography>
                            </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    const overriddenProps = { ...fields };
    if (getIsWebCrawler()) {
        overriddenProps.showAvailability = false;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <LoadingOverlay loading={isLoading}>
                {currentPage === "shopmybranch" && (
                    <Typography {...branchTitleStyle}>
                        {translate(`Recently Purchased and In Stock Now at the `)}
                        <Typography className="branchTitle">{homeBranch}</Typography>
                        {translate(` Branch`)}
                    </Typography>
                )}
                <Hidden below="md">
                    <GridContainer {...styles.cardGridContainer}>
                        {products.map(product => (
                            <>
                                {view && view === "Grid" ? (
                                    <GridItem {...styles.cardGridItem} key={product.id}>
                                        <StyledWrapper {...styles.gridCardDivider}>
                                            <ADIProductGridCard
                                                key={product.id}
                                                product={product}
                                                view={view ? view : "Grid"}
                                                showBrand={fields.showBrand}
                                                showPartNumbers={fields.showPartNumbers}
                                                showPrice={fields.showPrice}
                                                showAvailability={fields.showAvailability}
                                                showCompare={fields.showCompare}
                                                sourceName="Recently Purchased"
                                            />
                                        </StyledWrapper>
                                    </GridItem>
                                ) : (
                                    <GridItem {...styles.cardListItem} key={product.id}>
                                        <StyledWrapper {...styles.gridCardDivider}>
                                            {productLine && (
                                                <ADIProductListCard
                                                    key={product.id}
                                                    product={product}
                                                    view={view ? view : "Grid"}
                                                    showBrand={fields.showBrand}
                                                    showPartNumbers={fields.showPartNumbers}
                                                    showPrice={fields.showPrice}
                                                    showAvailability={fields.showAvailability}
                                                    productLine={productLine[product.id]}
                                                    showCompare={fields.showCompare}
                                                    sourceName="Recently Purchased"
                                                />
                                            )}
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                            </>
                        ))}
                    </GridContainer>
                </Hidden>
                <Hidden above="sm">
                    <GridContainer {...styles.cardGridContainer}>
                        {products.map(product => (
                            <GridItem {...styles.cardGridItem} key={product.id}>
                                <StyledWrapper {...styles.gridCardDivider}>
                                    <ADIProductGridCard
                                        key={product.id}
                                        product={product}
                                        view={view ? view : "Grid"}
                                        showBrand={fields.showBrand}
                                        showPartNumbers={fields.showPartNumbers}
                                        showPrice={fields.showPrice}
                                        showAvailability={fields.showAvailability}
                                        sourceName="Recently Purchased"
                                    />
                                </StyledWrapper>
                            </GridItem>
                        ))}
                    </GridContainer>
                    <Modal
                        headline={<Typography>{siteMessage("ProductionCompare_MaximumItemsReached")}</Typography>}
                        {...styles.compareWarningPopup}
                        isOpen={maximumCompareReached}
                        handleClose={closePopup}
                    >
                        <StyledWrapper>
                            <Typography>{translate(`Please 'Compare' or remove items.`)}</Typography>
                            <StyledWrapper {...styles.CompareButtonContainer}>
                                <Clickable href={productComparePageLink?.url}>
                                    <Button>{translate("Compare")}</Button>
                                </Clickable>
                            </StyledWrapper>
                        </StyledWrapper>
                    </Modal>
                </Hidden>
            </LoadingOverlay>
            {window.location.pathname.includes("shopmybranch") && <ProductCompareFlyOut />}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentlyPurchasedProductList),
    definition: {
        group: "My Account",
        displayName: "My Purchase History",
        fieldDefinitions: [
            {
                name: fields.showImage,
                displayName: "Show Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showBrand,
                displayName: "Show Brand",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Show Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showCompare,
                displayName: "Show Compare",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showAvailability,
                displayName: "Show Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showPrice,
                displayName: "Show Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.productListDefaultView,
                editorTemplate: "RadioButtonsField",
                defaultValue: "Grid",
                fieldType: "General",
                sortOrder: 10,
                options: [
                    {
                        displayName: "List",
                        value: "List",
                    },
                    {
                        displayName: "Grid",
                        value: "Grid",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
